import { Close, PlaylistAdd } from '@mui/icons-material';
import { Card, Checkbox, Divider, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { OutlinedButton } from '@shared/components';
import { AssortmentBinding, LocationMinimal } from './ConnectAssortmentsDialog';
import { Dispatch, SetStateAction } from 'react';

interface Props {
  assortment: AssortmentBinding;
  index: number;
  assortmentBindings: AssortmentBinding[];
  disabled: boolean;
  setAssortmentBindings: Dispatch<SetStateAction<AssortmentBinding[]>>;
  checkedLocations: LocationMinimal[];
  handleToggleLocation: (location: LocationMinimal) => void;
  resetCheckedLocations: () => void;
}

export function AssortmentCard({
  assortment,
  index,
  assortmentBindings,
  disabled,
  setAssortmentBindings,
  checkedLocations,
  handleToggleLocation,
  resetCheckedLocations,
}: Props) {
  const t = useMyAssortmentTFunction();

  const isLastAssortment = index === assortmentBindings.length - 1;

  const isEmpty = assortment.title.trim() === '';
  const isBlankError = isLastAssortment && isEmpty && !!assortment.isTouched;

  // Only new (editable) cards can be a duplicate error — existing assortment
  // names can't be changed, so they're allowed to collide.
  const cardHasDuplicate =
    !assortment.isExisting &&
    !isEmpty &&
    assortmentBindings.some(
      (a, idx) =>
        idx !== index && a.title.trim().toLowerCase() === assortment.title.trim().toLowerCase()
    );

  const cardHasError = isBlankError || cardHasDuplicate;

  const errorText = isBlankError
    ? t('myAssortment.memberConfig.error.requred')
    : cardHasDuplicate
    ? t('myAssortment.memberConfig.error.duplicate')
    : undefined;

  const handleUpdateName = (id: string, newName: string) => {
    setAssortmentBindings((prev) =>
      prev.map((assortment) =>
        assortment.id === id ? { ...assortment, title: newName } : assortment
      )
    );
  };

  const removeAssortment = (id: string) => {
    setAssortmentBindings((prevBindings) => prevBindings.filter((binding) => binding.id !== id));
  };

  const relocateLocations = (locations: LocationMinimal[], assortmentId: string) => {
    setAssortmentBindings((prev) =>
      prev.map((assortment) => {
        const currentLocationIds = assortment.locations.map((location) => location.id);
        const movingLocationIds = locations.map((location) => location.id);

        const uniqueNewLocations = locations.filter((loc) => !currentLocationIds.includes(loc.id));

        if (assortment.id === assortmentId) {
          return { ...assortment, locations: [...assortment.locations, ...uniqueNewLocations] };
        } else {
          return {
            ...assortment,
            locations: assortment.locations.filter((loc) => !movingLocationIds.includes(loc.id)),
          };
        }
      })
    );
    resetCheckedLocations();
  };

  return (
    <Card
      key={assortment.id}
      variant="outlined"
      sx={{
        width: 'fit-content',
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        maxWidth: 270,
        justifyContent: 'space-between',
        gap: 2,
        py: 1,
        px: 2,
      }}
    >
      <Stack
        display="grid"
        gridTemplateRows="0.5fr 0.1fr 1fr"
        height="100%"
        gap={2}
        alignItems="start"
      >
        <Stack gap={0.5}>
          <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">{t('myAssortment.memberConfig.assortmentName')}</Typography>
            <IconButton
              sx={{ p: 0 }}
              disabled={disabled || !!assortment.locations.length}
              onClick={() => removeAssortment(assortment.id)}
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>
          {!assortment.isExisting ? (
            <TextField
              size="xs"
              autoFocus
              disabled={disabled}
              value={assortment.title}
              error={!!cardHasError}
              helperText={errorText}
              onChange={(e) => handleUpdateName(assortment.id, e.target.value)}
              onBlur={() =>
                setAssortmentBindings((prev) =>
                  prev.map((a) => (a.id === assortment.id ? { ...assortment, isTouched: true } : a))
                )
              }
              sx={{ '& .MuiFormHelperText-root': { m: 0 } }}
            />
          ) : (
            <Typography variant="subtitle2">{assortment.title}</Typography>
          )}
        </Stack>
        <Divider />
        <Stack justifyContent="flex-start">
          <Typography variant="body2">{t('myAssortment.memberConfig.locations')}</Typography>
          {assortment.locations.map((location) => {
            return (
              <Stack key={location.id} flexDirection="row" gap={1} alignItems="center">
                <Checkbox
                  value={location.id}
                  disabled={disabled || !!cardHasError}
                  checked={checkedLocations.map((loc) => loc.id).includes(location.id)}
                  onClick={() => handleToggleLocation(location)}
                />
                <Typography variant="subtitle2">{location.name}</Typography>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
      <OutlinedButton
        sx={{ width: 'fit-content', alignSelf: 'flex-end', height: 28 }}
        disabled={disabled || !checkedLocations.length || !!cardHasError}
        size="small"
        startIcon={<PlaylistAdd />}
        onClick={() => relocateLocations(checkedLocations, assortment.id)}
      >
        {t('myAssortment.memberConfig.moveHere')}
      </OutlinedButton>
    </Card>
  );
}
